import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import Select, { type StylesConfig } from 'react-select';
import { buildReportSelectStyles } from '@/components/Reports/reportSelectStyles';
import * as XLSX from 'xlsx';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import ReportService from '@services/ReportService';
import { ClearIndicatorComponent as ClearIndicator } from '@/components/Reports/ClearIndicatorComponent';
import './ArtistReport.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SelectOption {
  value: number | string;
  label: string;
}

/** Raw row from ArtistGetCaptureTime */
interface ArtistRow {
  PersonId: number;
  Name: string;
  Team: string;
  Project: string;
  TaskName: string;
  Phase: string;
  WorkingDate: string;
  CaptureDate: string;
  CaptureTime: string;
  Hours: number;
  ExternalEstimated: string;
  Estimated: string;
  ConversationId: number;
  CardType: string;
  Actual: string;
  Progress: string;
  ArtistCategory: string;
  Department: string;
}

/** Derived row with computed fields */
interface DerivedRow extends ArtistRow {
  actualCalculated: number;
  remaining: number;
  sold: number;
  scrum: number;
  burn: number;
  captureDT: Date;
}

type TabId = 'project' | 'team' | 'user';
type SortDir = 'asc' | 'desc';

// ─── Constants ────────────────────────────────────────────────────────────────

const CHART_COLORS = {
  sold: '#508ca6',
  burn: '#c09060',
  hours: '#6b7280',
  scrum: '#6a8caf',
};

const PIE_COLORS = ['#508ca6', '#62accb', '#4a7a95', '#3d6880', '#82b4cc', '#a0c8de'];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getTimeZoneOffset(): string
{
  const offsetMinutes = new Date().getTimezoneOffset();
  const offsetHours = offsetMinutes / 60;
  const sign = offsetHours < 0 ? '+' : '-';
  return `${sign}${Math.abs(Math.floor(offsetHours))}`;
}

function calculateDerivedFields(data: ArtistRow[]): DerivedRow[]
{
  return data.map((row, _idx, arr) =>
  {
    const captureDT = new Date(row.CaptureDate + ' ' + row.CaptureTime);
    const est = parseFloat(String(row.Estimated)) || 0;
    const actualCalculated = arr
      .filter(
        r =>
          r.ConversationId === row.ConversationId &&
          r.Phase === row.Phase &&
          new Date(r.CaptureDate + ' ' + r.CaptureTime) <= captureDT,
      )
      .reduce((sum, r) => sum + r.Hours, 0);
    const remaining = est - actualCalculated;
    let sold = 0;
    if (row.CardType !== 'Routine')
    {
      if (remaining >= 0) sold = row.Hours;
      else if (-remaining < row.Hours) sold = row.Hours + remaining;
    }
    let scrum = 0;
    if (row.CardType !== 'MPC')
    {
      if (remaining >= 0) scrum = row.Hours;
      else if (-remaining < row.Hours) scrum = row.Hours + remaining;
    }
    const burn = row.CardType === 'Routine' ? row.Hours - scrum : row.Hours - sold;
    return { ...row, actualCalculated, remaining, sold, scrum, burn, captureDT };
  });
}

const MONTH_NAMES = [
  'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
  'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER',
];

/** Parses only the date part (YYYY-MM-DD) of an ISO string, ignoring time/timezone
 *  so dates near midnight don't shift to the previous day in local time. */
function getDateParts(dateStr: string): { year: number; month: number; day: number }
{
  const datePart = dateStr.split('T')[0] || '';
  const parts = datePart.split('-').map(Number);
  return { year: parts[0] || 0, month: parts[1] || 1, day: parts[2] || 1 };
}

function getMonthLabel(dateStr: string): string
{
  const { year, month } = getDateParts(dateStr);
  return `${MONTH_NAMES[month - 1]} ${year}`;
}

/** Reverses getMonthLabel into a value that sorts chronologically. */
function getMonthSortKey(monthLabel: string): number
{
  const [name, yearStr] = monthLabel.split(' ');
  const monthIndex = MONTH_NAMES.indexOf(name || '');
  return Number(yearStr) * 12 + monthIndex;
}

// ─── Custom date input ────────────────────────────────────────────────────────

interface ArpDateInputProps {
  value?: string;
  onClick?: () => void;
  onClear: () => void;
}

const ArpDateInput = React.forwardRef<HTMLInputElement, ArpDateInputProps>(
  ({ value, onClick, onClear }, ref) => (
    <div className="arp-date-input-wrapper">
      <input
        ref={ref}
        type="text"
        value={value || ''}
        onClick={onClick}
        readOnly
        className="arp-date-input"
        placeholder="MM/DD/YYYY"
        onChange={() => {}}
      />
      <button
        type="button"
        className="arp-date-icon-btn"
        onClick={value ? onClear : onClick}
        tabIndex={-1}
      >
        <i className="fa fa-calendar" style={{ fontSize: 12 }} />
      </button>
    </div>
  ),
);
ArpDateInput.displayName = 'ArpDateInput';

// ─── Sort Icon ────────────────────────────────────────────────────────────────

const SortIcon: React.FC<{ col: string; sorted: string; dir: SortDir }> = ({
  col,
  sorted,
  dir,
}) => (
  <i
    className={
      sorted === col ? (dir === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc') : 'fa fa-sort'
    }
    style={{ display: 'inline-block', width: 20 }}
  />
);

// ─── Sub-report: By Project ───────────────────────────────────────────────────

interface ProjectReportProps {
  filteredData: DerivedRow[];
  isDark: boolean;
  fromDate: string;
  toDate: string;
  hasSearched: boolean;
}

const ProjectReport: React.FC<ProjectReportProps> = ({
  filteredData,
  isDark,
  fromDate,
  toDate,
  hasSearched,
}) =>
{
  const tickStyle = { fontSize: 11, fill: isDark ? '#a9a093' : '#666' };
  const tooltipStyle = {
    backgroundColor: isDark ? '#3a3c3c' : '#fff',
    border: `1px solid ${isDark ? '#4a4c4c' : '#ccc'}`,
    color: isDark ? '#a9a093' : '#333',
    fontSize: 11,
  };

  // Group by month, sorted chronologically. Months within the selected range
  // that have no data still appear, labeled as empty, instead of being skipped.
  const months = useMemo(() =>
  {
    const map = new Map<string, DerivedRow[]>();
    filteredData.forEach(row =>
    {
      const key = getMonthLabel(row.WorkingDate);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(row);
    });

    if (hasSearched && fromDate && toDate)
    {
      const cursor = moment(fromDate).startOf('month');
      const end = moment(toDate).startOf('month');
      while (cursor.isSameOrBefore(end))
      {
        const key = `${MONTH_NAMES[cursor.month()]} ${cursor.year()}`;
        if (!map.has(key)) map.set(key, []);
        cursor.add(1, 'month');
      }
    }

    return Array.from(map.entries()).sort(
      ([a], [b]) => getMonthSortKey(a) - getMonthSortKey(b),
    );
  }, [filteredData, fromDate, toDate, hasSearched]);

  return (
    <div>
      {months.map(([month, rows]) =>
      {
        if (rows.length === 0)
        {
          return (
            <div key={month} className="arp-month-group">
              <div className="arp-month-title">{month}</div>
              <div className="arp-month-no-data">
                <i className="material-icons">inbox</i>
                Record not found
              </div>
            </div>
          );
        }

        // Aggregate by project
        const projectMap = new Map<
          string,
          { actual: number; sold: number; burn: number; dummy: number }
        >();
        rows.forEach(r =>
        {
          const existing = projectMap.get(r.Project) || { actual: 0, sold: 0, burn: 0, dummy: 0 };
          projectMap.set(r.Project, {
            actual: existing.actual + r.actualCalculated,
            sold: existing.sold + r.sold,
            burn: existing.burn + r.burn,
            dummy: existing.dummy,
          });
        });
        const projects = Array.from(projectMap.entries()).map(([name, v]) => ({
          name,
          actual: parseFloat(v.actual.toFixed(2)),
          sold: parseFloat(v.sold.toFixed(2)),
          burn: parseFloat(v.burn.toFixed(2)),
          dummy: parseFloat(v.dummy.toFixed(2)),
          soldDummy: parseFloat(v.sold.toFixed(2)),
          valid: v.burn === 0,
        }));

        const chartData = projects.map(p => ({
          name: p.name.length > 18 ? p.name.slice(0, 16) + '…' : p.name,
          Sold: p.sold,
          Burn: p.burn,
        }));

        return (
          <div key={month} className="arp-month-group">
            <div className="arp-month-title">{month}</div>
            <table className="arp-summary-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Actual</th>
                  <th style={{ color: CHART_COLORS.sold }}>Sold</th>
                  <th style={{ color: CHART_COLORS.burn }}>Burn</th>
                  <th>Dummy</th>
                  <th style={{ color: CHART_COLORS.sold }}>Sold-Dummy</th>
                  <th>Valid</th>
                </tr>
              </thead>
              <tbody>
                {projects.map(p => (
                  <tr key={p.name}>
                    <td>{p.name}</td>
                    <td>{p.actual.toFixed(2)}</td>
                    <td style={{ color: CHART_COLORS.sold }}>{p.sold.toFixed(2)}</td>
                    <td style={{ color: CHART_COLORS.burn }}>{p.burn.toFixed(2)}</td>
                    <td>{p.dummy.toFixed(2)}</td>
                    <td style={{ color: CHART_COLORS.sold }}>{p.soldDummy.toFixed(2)}</td>
                    <td>
                      {p.valid ? (
                        <i className="material-icons arp-valid-icon">check</i>
                      ) : (
                        <span style={{ color: '#dc2626' }}>✗</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="arp-chart-container">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <XAxis dataKey="name" tick={tickStyle} />
                  <YAxis tick={tickStyle} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend
                    iconSize={12}
                    wrapperStyle={{ fontSize: 11, color: isDark ? '#a9a093' : undefined }}
                  />
                  <Bar dataKey="Sold" fill={CHART_COLORS.sold} maxBarSize={60} />
                  <Bar dataKey="Burn" fill={CHART_COLORS.burn} maxBarSize={60} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        );
      })}
      {!hasSearched && filteredData.length === 0 && (
        <div className="arp-no-data">Apply filters and click "Apply Filters" to see results.</div>
      )}
    </div>
  );
};

// ─── Sub-report: By Team ──────────────────────────────────────────────────────

interface TeamReportProps {
  filteredData: DerivedRow[];
  isDark: boolean;
}

const TeamReport: React.FC<TeamReportProps> = ({ filteredData, isDark }) =>
{
  const tickStyle = { fontSize: 11, fill: isDark ? '#a9a093' : '#666' };
  const tooltipStyle = {
    backgroundColor: isDark ? '#3a3c3c' : '#fff',
    border: `1px solid ${isDark ? '#4a4c4c' : '#ccc'}`,
    color: isDark ? '#a9a093' : '#333',
    fontSize: 11,
  };

  // Aggregate by team
  const teams = useMemo(() =>
  {
    const map = new Map<
      string,
      { hours: number; sold: number; burn: number; scrum: number; members: Set<number> }
    >();
    filteredData.forEach(r =>
    {
      const t = r.Team || 'N/A';
      const existing = map.get(t) || { hours: 0, sold: 0, burn: 0, scrum: 0, members: new Set() };
      map.set(t, {
        hours: existing.hours + r.Hours,
        sold: existing.sold + r.sold,
        burn: existing.burn + r.burn,
        scrum: existing.scrum + r.scrum,
        members: existing.members.add(r.PersonId),
      });
    });
    return Array.from(map.entries()).map(([name, v]) => ({
      name,
      hours: parseFloat(v.hours.toFixed(2)),
      sold: parseFloat(v.sold.toFixed(2)),
      burn: parseFloat(v.burn.toFixed(2)),
      scrum: parseFloat(v.scrum.toFixed(2)),
      memberCount: v.members.size,
    }));
  }, [filteredData]);

  const chartData = teams.map(t => ({
    name: t.name,
    Hours: t.hours,
    Sold: t.sold,
    Burn: t.burn,
  }));

  return (
    <div>
      <div className="arp-team-cards">
        {teams.map(team =>
        {
          const ratio = team.hours > 0 ? (team.sold / team.hours) * 100 : 0;
          return (
            <div key={team.name} className="arp-team-card">
              <div className="arp-team-card-header">
                <span className="arp-team-card-name">Team {team.name}</span>
                <span className="arp-team-card-members">{team.memberCount} members</span>
              </div>
              <div className="arp-team-card-row">
                <span>Total Hours:</span>
                <span>{team.hours}</span>
              </div>
              <div className="arp-team-card-row arp-team-card-row--sold">
                <span>Sold:</span>
                <span>{team.sold.toFixed(1)}</span>
              </div>
              <div className="arp-team-card-row arp-team-card-row--burn">
                <span>Burn:</span>
                <span>{team.burn.toFixed(1)}</span>
              </div>
              <div className="arp-team-ratio-bar-wrapper">
                <div className="arp-team-ratio-bar">
                  <div
                    className="arp-team-ratio-bar-fill"
                    style={{ width: `${Math.min(ratio, 100)}%` }}
                  />
                </div>
                <div className="arp-team-ratio-label">Sold vs Burn ratio</div>
              </div>
            </div>
          );
        })}
      </div>
      {teams.length > 0 && (
        <div className="arp-chart-container">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <XAxis dataKey="name" tick={tickStyle} />
              <YAxis tick={tickStyle} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend
                iconSize={12}
                wrapperStyle={{ fontSize: 11, color: isDark ? '#a9a093' : undefined }}
              />
              <Bar dataKey="Hours" fill={CHART_COLORS.hours} maxBarSize={60} />
              <Bar dataKey="Sold" fill={CHART_COLORS.sold} maxBarSize={60} />
              <Bar dataKey="Burn" fill={CHART_COLORS.burn} maxBarSize={60} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      {filteredData.length === 0 && (
        <div className="arp-no-data">Apply filters and click "Apply Filters" to see results.</div>
      )}
    </div>
  );
};

// ─── Sub-report: By User ──────────────────────────────────────────────────────

interface UserReportProps {
  filteredData: DerivedRow[];
  isDark: boolean;
}

const UserReport: React.FC<UserReportProps> = ({ filteredData, isDark }) =>
{
  const tickStyle = { fontSize: 11, fill: isDark ? '#a9a093' : '#666' };
  const tooltipStyle = {
    backgroundColor: isDark ? '#3a3c3c' : '#fff',
    border: `1px solid ${isDark ? '#4a4c4c' : '#ccc'}`,
    color: isDark ? '#a9a093' : '#333',
    fontSize: 11,
  };

  const users = useMemo(() =>
  {
    const map = new Map<
      number,
      {
        name: string;
        team: string;
        records: number;
        hours: number;
        sold: number;
        scrum: number;
        burn: number;
      }
    >();
    filteredData.forEach(r =>
    {
      const existing = map.get(r.PersonId) || {
        name: r.Name,
        team: r.Team,
        records: 0,
        hours: 0,
        sold: 0,
        scrum: 0,
        burn: 0,
      };
      map.set(r.PersonId, {
        ...existing,
        records: existing.records + 1,
        hours: existing.hours + r.Hours,
        sold: existing.sold + r.sold,
        scrum: existing.scrum + r.scrum,
        burn: existing.burn + r.burn,
      });
    });
    return Array.from(map.values()).map(u => ({
      ...u,
      hours: parseFloat(u.hours.toFixed(2)),
      sold: parseFloat(u.sold.toFixed(1)),
      scrum: parseFloat(u.scrum.toFixed(1)),
      burn: parseFloat(u.burn.toFixed(1)),
    }));
  }, [filteredData]);

  const pieData = users.map((u, i) => ({ name: u.name, value: u.hours, index: i }));

  const barData = users.map(u => ({
    name: u.name.split(' ').slice(0, 2).join(' '),
    Sold: u.sold,
    Burn: u.burn,
  }));

  return (
    <div>
      <table className="arp-summary-table" style={{ marginBottom: 16 }}>
        <thead>
          <tr>
            <th>Name</th>
            <th>Team</th>
            <th>Records</th>
            <th>Hours</th>
            <th style={{ color: CHART_COLORS.sold }}>Sold</th>
            <th style={{ color: CHART_COLORS.scrum }}>SCRUM</th>
            <th style={{ color: CHART_COLORS.burn }}>Burn</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u, i) => (
            <tr key={i}>
              <td>{u.name}</td>
              <td>{u.team}</td>
              <td>{u.records}</td>
              <td>{u.hours}</td>
              <td style={{ color: CHART_COLORS.sold }}>{u.sold.toFixed(1)}</td>
              <td style={{ color: CHART_COLORS.scrum }}>{u.scrum.toFixed(1)}</td>
              <td style={{ color: CHART_COLORS.burn }}>{u.burn.toFixed(1)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="arp-user-charts-row">
        <div>
          <div className="arp-chart-label">Hours by Person</div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie dataKey="value" data={pieData} outerRadius={80} label={false}>
                {pieData.map((_, index) => (
                  <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} formatter={v => `${v} hrs`} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div>
          <div className="arp-chart-label">Sold vs Burn by Person</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              layout="vertical"
              data={barData}
              margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
            >
              <XAxis type="number" tick={tickStyle} />
              <YAxis type="category" dataKey="name" tick={tickStyle} width={100} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend
                iconSize={12}
                wrapperStyle={{ fontSize: 11, color: isDark ? '#a9a093' : undefined }}
              />
              <Bar dataKey="Sold" fill={CHART_COLORS.sold} maxBarSize={20} />
              <Bar dataKey="Burn" fill={CHART_COLORS.burn} maxBarSize={20} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {filteredData.length === 0 && (
        <div className="arp-no-data">Apply filters and click "Apply Filters" to see results.</div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const ArtistReport: React.FC = () =>
{
  const isDark = document.documentElement.classList.contains('dark');
  const selectStyles = buildReportSelectStyles(isDark);

  // Options
  const [teamOptions, setTeamOptions] = useState<SelectOption[]>([]);
  const [projectOptions, setProjectOptions] = useState<SelectOption[]>([]);
  const [cardTypeOptions, setCardTypeOptions] = useState<SelectOption[]>([]);

  // Filters
  const [selectedTeams, setSelectedTeams] = useState<SelectOption[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<SelectOption[]>([]);
  const [selectedCardTypes, setSelectedCardTypes] = useState<SelectOption[]>([]);
  const [fromDate, setFromDate] = useState(moment().startOf('month').format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState(moment().format('YYYY-MM-DD'));

  // State
  const [loading, setLoading] = useState(false);
  const [filteredData, setFilteredData] = useState<DerivedRow[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>('project');

  // Table sort
  const [sortCol, setSortCol] = useState('');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Date range notification
  const [showDateRangeMsg, setShowDateRangeMsg] = useState(false);
  const dateRangeMsgTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Initial data load ────────────────────────────────────────────────────────

  useEffect(() =>
  {
    document.title = 'Corgee';
  }, []);

  useEffect(() =>
  {
    ReportService.GetAllTeams()
      .then((data: any[]) =>
        setTeamOptions((data || []).map(t => ({ value: t.BasicFieldValueId, label: t.Value }))),
      )
      .catch(() => {});

    ReportService.GetAllProject({ GroupByDetailId: 0, GroupByLayerId: 0, GroupByValueField: '' })
      .then((data: any) =>
        setProjectOptions(
          (data?.Projects || []).map((p: any) => ({ value: p.ProjectID, label: p.Name })),
        ),
      )
      .catch(() => {});

    ReportService.GetAllCard()
      .then((data: any[]) =>
        setCardTypeOptions((data || []).map(c => ({ value: c.SideMenuId, label: c.Text }))),
      )
      .catch(() => {});
  }, []);

  // ── Apply filters ─────────────────────────────────────────────────────────────

  const applyFilters = useCallback(async () =>
  {
    if (!fromDate || !toDate) return;
    if (moment(toDate).isBefore(moment(fromDate))) return;
    if (moment(fromDate).isBefore(moment().subtract(2, 'years')))
    {
      if (dateRangeMsgTimerRef.current) clearTimeout(dateRangeMsgTimerRef.current);
      setShowDateRangeMsg(true);
      dateRangeMsgTimerRef.current = setTimeout(() => setShowDateRangeMsg(false), 3000);
      return;
    }

    setLoading(true);
    setFilteredData([]);

    try
    {
      const request = {
        Teams: selectedTeams.map(t => t.value).join(','),
        projectId: selectedProjects.map(p => p.value).join(','),
        sideMenuId: selectedCardTypes.map(c => c.value).join(','),
        DateTime: moment(fromDate).format('YYYYMMDD'),
        Duedate: moment(toDate).format('YYYYMMDD'),
        timeZone: getTimeZoneOffset(),
      };

      const raw: ArtistRow[] = await ReportService.GetArtistCaptureTime(request);
      const derived = calculateDerivedFields(raw || []);
      setFilteredData(derived);
      setHasSearched(true);
    }
    catch (e)
    {
      console.error('ArtistReport error:', e);
    }

    setLoading(false);
  }, [selectedTeams, selectedProjects, selectedCardTypes, fromDate, toDate]);

  // ── KPI totals ────────────────────────────────────────────────────────────────

  const kpis = useMemo(() =>
  {
    const totalHours = filteredData.reduce((s, r) => s + r.Hours, 0);
    const totalSold = filteredData.reduce((s, r) => s + r.sold, 0);
    const totalBurn = filteredData.reduce((s, r) => s + r.burn, 0);
    return {
      totalHours: totalHours.toFixed(2),
      totalSold: totalSold.toFixed(1),
      totalBurn: totalBurn.toFixed(1),
      records: filteredData.length,
    };
  }, [filteredData]);

  // ── Sorting ───────────────────────────────────────────────────────────────────

  const sortedData = useMemo(() =>
  {
    if (!sortCol) return filteredData;
    return [...filteredData].sort((a, b) =>
    {
      const av = (a as any)[sortCol] ?? '';
      const bv = (b as any)[sortCol] ?? '';
      if (typeof av === 'number' && typeof bv === 'number')
        return sortDir === 'asc' ? av - bv : bv - av;
      return sortDir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
  }, [filteredData, sortCol, sortDir]);

  const handleSort = (col: string) =>
  {
    const dir = sortCol === col && sortDir === 'asc' ? 'desc' : 'asc';
    setSortCol(col);
    setSortDir(dir);
  };

  // ── Export ────────────────────────────────────────────────────────────────────

  const exportToExcel = () =>
  {
    if (!filteredData.length) return;
    const headers = [
      'EmployeeID',
      'Name',
      'Team',
      'Phase',
      'CardType',
      'Hours',
      'Actual',
      'Remaining',
      'Sold',
      'SCRUM',
      'Burn',
    ];
    const rows = filteredData.map(r => ({
      EmployeeID: r.PersonId,
      Name: r.Name,
      Team: r.Team,
      Phase: r.Phase,
      CardType: r.CardType,
      Hours: r.Hours,
      Actual: r.actualCalculated,
      Remaining: r.remaining,
      Sold: r.sold,
      SCRUM: r.scrum,
      Burn: parseFloat(r.burn.toFixed(1)),
    }));
    const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'ArtistReport');
    XLSX.writeFile(wb, 'ArtistReport.xlsx');
  };

  // ── Tabs config ────────────────────────────────────────────────────────────────

  const tabs: { id: TabId; label: string; icon: string }[] = [
    { id: 'project', label: 'By Project', icon: 'work' },
    { id: 'team', label: 'By Team', icon: 'group' },
    { id: 'user', label: 'By User', icon: 'layers' },
  ];

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <>
      <div id="ArtistReport">
        {/* Header */}
        <h3 className="arp-title">Artist Report</h3>
        <p className="arp-subtitle">
          This report contains information on the hours logged by the users in their cards.
        </p>

        {/* Filters */}
        <div className="arp-filters">
          <div className="arp-filter-row">
            <div className="arp-filter-item">
              <label className="arp-label">Team(s):</label>
              <Select
                isMulti
                options={teamOptions}
                value={selectedTeams}
                onChange={v => setSelectedTeams((v as SelectOption[]) || [])}
                placeholder="Select..."
                styles={selectStyles as StylesConfig<SelectOption, true>}
                closeMenuOnSelect={false}
                components={{ ClearIndicator }}
              />
            </div>
            <div className="arp-filter-item">
              <label className="arp-label">Project(s):</label>
              <Select
                isMulti
                options={projectOptions}
                value={selectedProjects}
                onChange={v => setSelectedProjects((v as SelectOption[]) || [])}
                placeholder="Select..."
                styles={selectStyles as StylesConfig<SelectOption, true>}
                closeMenuOnSelect={false}
                components={{ ClearIndicator }}
              />
            </div>
            <div className="arp-filter-item">
              <label className="arp-label">Card Type(s):</label>
              <Select
                isMulti
                options={cardTypeOptions}
                value={selectedCardTypes}
                onChange={v => setSelectedCardTypes((v as SelectOption[]) || [])}
                placeholder="Select..."
                styles={selectStyles as StylesConfig<SelectOption, true>}
                closeMenuOnSelect={false}
                components={{ ClearIndicator }}
              />
            </div>
            <div className="arp-filter-item arp-filter-item--date">
              <label className="arp-label">From:</label>
              <DatePicker
                selected={fromDate ? moment(fromDate, 'YYYY-MM-DD').toDate() : null}
                onChange={(d: Date | null) =>
                {
                  setFromDate(d ? moment(d).format('YYYY-MM-DD') : '');
                  setHasSearched(false);
                }}
                dateFormat="MM/dd/yyyy"
                maxDate={new Date()}
                customInput={<ArpDateInput onClear={() => { setFromDate(''); setHasSearched(false); }} />}
                popperProps={{ strategy: 'fixed' }}
                showPopperArrow={false}
                calendarClassName="arp-datepicker-calendar"
              />
            </div>
            <div className="arp-filter-item arp-filter-item--date">
              <label className="arp-label">To:</label>
              <DatePicker
                selected={toDate ? moment(toDate, 'YYYY-MM-DD').toDate() : null}
                onChange={(d: Date | null) =>
                {
                  setToDate(d ? moment(d).format('YYYY-MM-DD') : '');
                  setHasSearched(false);
                }}
                maxDate={new Date(Date.now())}
                dateFormat="MM/dd/yyyy"
                customInput={<ArpDateInput onClear={() => { setToDate(''); setHasSearched(false); }} />}
                popperProps={{ strategy: 'fixed' }}
                showPopperArrow={false}
                calendarClassName="arp-datepicker-calendar"
              />
            </div>
            <div className="arp-filter-item arp-filter-item--action">
              <label className="arp-label">&nbsp;</label>
              <button className="arp-apply-btn" onClick={applyFilters} disabled={loading}>
                {loading ? 'Loading...' : 'Apply Filters'}
              </button>
            </div>
          </div>
        </div>

        {/* KPI row */}
        {(filteredData.length > 0 || loading) && (
          <div className="arp-kpi-row">
            <div className="arp-kpi-item">
              <div className="arp-kpi-label">Total Hours</div>
              <div className="arp-kpi-value">
                {kpis.totalHours}
                <span className="arp-kpi-unit">hrs</span>
              </div>
            </div>
            <div className="arp-kpi-item">
              <div className="arp-kpi-label">Total Sold</div>
              <div className="arp-kpi-value">
                {kpis.totalSold}
                <span className="arp-kpi-unit">hrs</span>
              </div>
            </div>
            <div className="arp-kpi-item">
              <div className="arp-kpi-label">Total Burn</div>
              <div className="arp-kpi-value">
                {kpis.totalBurn}
                <span className="arp-kpi-unit">hrs</span>
              </div>
            </div>
            <div className="arp-kpi-item">
              <div className="arp-kpi-label">Records</div>
              <div className="arp-kpi-value">{kpis.records}</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="arp-tabs-row">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`arp-tab-btn${activeTab === tab.id ? ' arp-tab-btn--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="material-icons arp-tab-icon">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
          <button
            className="arp-export-btn"
            onClick={exportToExcel}
            title="Export to Excel"
            disabled={!filteredData.length}
          >
            <span className="material-icons style-icon-cloud">cloud_download</span>
            Export
          </button>
        </div>

        {/* Report content */}
        <div className="arp-report-content">
          {loading ? (
            <div className="arp-loading">
              <div className="spinner-border" role="status" style={{ width: 20, height: 20 }} />
              Loading...
            </div>
          ) : (
            <>
              {activeTab === 'project' && (
                <ProjectReport
                  filteredData={filteredData}
                  isDark={isDark}
                  fromDate={fromDate}
                  toDate={toDate}
                  hasSearched={hasSearched}
                />
              )}
              {activeTab === 'team' && <TeamReport filteredData={filteredData} isDark={isDark} />}
              {activeTab === 'user' && <UserReport filteredData={filteredData} isDark={isDark} />}
            </>
          )}
        </div>

        {/* Record Details */}
        <div className="arp-details-section">
          <div className="arp-details-title">Record Details</div>
          <div className="arp-table-scroll">
            <table className="arp-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('PersonId')}>
                    EmployeeId <SortIcon col="PersonId" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('Name')}>
                    Employee <SortIcon col="Name" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('Team')}>
                    Team <SortIcon col="Team" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('Phase')}>
                    Phase <SortIcon col="Phase" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('CardType')}>
                    Card Type <SortIcon col="CardType" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('Hours')}>
                    Hours <SortIcon col="Hours" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('actualCalculated')}>
                    Actual <SortIcon col="actualCalculated" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('remaining')}>
                    Remaining <SortIcon col="remaining" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('sold')}>
                    Sold <SortIcon col="sold" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('scrum')}>
                    SCRUM <SortIcon col="scrum" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('burn')}>
                    Burn <SortIcon col="burn" sorted={sortCol} dir={sortDir} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((row, i) => (
                  <tr key={i}>
                    <td>{row.PersonId}</td>
                    <td>{row.Name}</td>
                    <td>{row.Team}</td>
                    <td>{row.Phase}</td>
                    <td>{row.CardType}</td>
                    <td>{row.Hours}</td>
                    <td>{row.actualCalculated}</td>
                    <td className={row.remaining < 0 ? 'arp-td--negative' : 'arp-td--positive'}>
                      {row.remaining}
                    </td>
                    <td className="arp-td--sold">{row.sold}</td>
                    <td className="arp-td--scrum">{row.scrum}</td>
                    <td className="arp-td--burn">{row.burn.toFixed(1)}</td>
                  </tr>
                ))}
                {!loading && filteredData.length === 0 && (
                  <tr>
                    <td colSpan={11} className="arp-no-data">
                      No records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showDateRangeMsg &&
        createPortal(
          <>
            <style>{`
            @keyframes arp-date-msg {
              0%   { opacity: 0; }
              10%  { opacity: 1; }
              90%  { opacity: 1; }
              100% { opacity: 0; }
            }
          `}</style>
            <div
              style={{
                position: 'fixed',
                top: '64px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: '#62abcb',
                color: '#fff',
                padding: '8px 20px',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 500,
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                pointerEvents: 'none',
                zIndex: 9999,
                whiteSpace: 'nowrap',
                animation: 'arp-date-msg 3s ease forwards',
              }}
            >
              The start date must be within the last 2 years
            </div>
          </>,
          document.body,
        )}
    </>
  );
};

export default ArtistReport;
